import { CSSProperties } from "react";
import { IObject } from "../constants/interface";
import { filterObjEmptyValue } from "../utils/utils";
import { SelectTabFieldNamesType, SelectTabOption, DEFAULT_FIELD_NAMES, SelectTabInOption, SelectTabItem, SelectTabValue, OTHER_TAB_KEY, } from "./interface";

/**
 * 根据tab生成选项数据
 * @param options 外部选项
 * @param tabItem tab属性
 * @param fieldNames 字典映射
 * @returns 
 */
export function formatOptions(options?: SelectTabOption[], tabItem?: SelectTabItem, fieldNames?: SelectTabFieldNamesType): SelectTabInOption[] {
  if (!options) return [];
  const labelName = fieldNames?.label || DEFAULT_FIELD_NAMES.label;
  const subLabelName = fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel;
  const valueName = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
  const iconName = fieldNames?.icon || DEFAULT_FIELD_NAMES.icon;
  if (!tabItem || tabItem.value === valueName) {
    const newOptions = options.map(option => {
      return {
        label: option[labelName],
        subLabel: option[subLabelName],
        value: option[valueName],
        icon: option[iconName],
      };
    });
    return newOptions;
  }
  const formatObj: any = {};
  options.forEach(option => {
    const { [labelName]: label, [subLabelName]: subLabel, [valueName]: value, [iconName]: icon } = option;
    const groupValue = option[tabItem.value] || OTHER_TAB_KEY;
    if (!formatObj[groupValue]) {
      formatObj[groupValue] = {
        label: option[tabItem.groupLabel || tabItem.value] || groupValue,
        value: groupValue,
        subLabel: tabItem.groupSubLabel && option[tabItem.groupSubLabel],
        icon: tabItem.groupIcon && option[tabItem.groupIcon],
        children: [],
      };
    }
    formatObj[groupValue].children.push({
      label,
      subLabel,
      value,
      icon: tabItem.hideChildrenIcon ? null : icon,
    });
  });
  const newOptions: SelectTabInOption[] = Object.values(formatObj);
  return newOptions.map(item => {
    return {
      ...item,
      children: item.children?.sort((a, b) => {
        const aLabel = a.label;
        const bLabel = b.label;
        if (typeof aLabel === 'string' && typeof bLabel === 'string') {
          return aLabel?.localeCompare?.(bLabel, 'zh-CN');
        }
        return 0;
      }),
    };
  });
}
/** 设置选项样式 */
export function setOptionStyle(columnNumber: number, index: number): CSSProperties {
  const paddingWidth = columnNumber > 1 ? (((columnNumber - 1) * 12) / columnNumber) : 0;
  const cloIndex = index + 1;
  return {
    width: `calc(100% - ${(1 / columnNumber) * 100}% - ${paddingWidth}px)`,
    marginRight: cloIndex % columnNumber ? '12px' : 0,
  };
};
/** 判断改选项是否被选中 */
export function isOptionSelect(value?: SelectTabValue, optionValue?: string) {
  if (value === '' || value === undefined || value === null) return false;
  if (optionValue === '' || optionValue === undefined || optionValue === null) return false;
  if (Array.isArray(value)) {
    return value.includes(optionValue);
  }
  return value === optionValue;
}
/**
 * 过滤搜索选项
 * @param options 选项
 * @param filterValue 过滤字符串
 * @param fieldNames 字段映射
 * @param searchDiscriminateCase 是否区分大小写
 * @returns 
 */
export function filterSearchOptions(
  options?: SelectTabOption[],
  filterValue?: string,
  fieldNames?: SelectTabFieldNamesType,
  searchDiscriminateCase?: boolean,
) {
  if (!options) return [];
  if (!filterValue) return options;
  const labelName = fieldNames?.label || DEFAULT_FIELD_NAMES.label;
  const subLabelName = fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel;
  const newOptions: SelectTabOption[] = [];
  options.forEach(option => {
    const label = option[labelName];
    const subLabel = option[subLabelName];
    if (searchDiscriminateCase) {
      if (label?.includes(filterValue) || subLabel?.includes(filterValue)) {
        newOptions.push({
          ...option,
          [labelName]: label,
          [subLabelName]: subLabel,
        });
      }
    } else {
      if (label?.toLowerCase?.().includes(filterValue.toLowerCase()) || subLabel?.toLowerCase?.().includes(filterValue.toLowerCase())) {
        newOptions.push({
          ...option,
          [labelName]: label,
          [subLabelName]: subLabel,
        });
      }
    }
  });
  return newOptions;
}
/**
 * 获取value对应的option集合
 * @param value 值
 * @param options 选项
 * @param fieldNames 字段映射
 * @returns 
 */
export function getSelectTabOptions(
  value?: SelectTabValue,
  options?: SelectTabOption[],
  fieldNames?: SelectTabFieldNamesType,
): SelectTabOption[] {
  if (!value || !options) return [];
  const valueName = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
  if (Array.isArray(value)) {
    const filterOptions: SelectTabOption[] = options.filter(item => value.includes(item[valueName]));
    return filterOptions;
  }
  const filterOptions = options.find(item => item[valueName] === value);
  return filterOptions ? [filterOptions] : [];
}
/**
 * 拉平数组
 * @param arr
 * @param childrenKey
 */
export function flatSelectTabOptions(options: SelectTabInOption[]): any[] {
  let result: any[] = [];
  options.forEach(item => {
    const children = item.children;
    if (Array.isArray(children)) {
      result.push(...flatSelectTabOptions(children));
    } else {
      result.push(item);
    }
  });
  return result;
};
/**
 * 选项排序
 * @param options 选项
 * @returns 
 */
export function sortOptions(options: SelectTabInOption[], sort: (a: SelectTabInOption, b: SelectTabInOption) => number): SelectTabInOption[] {
  if (Array.isArray(options) && options.length) {
    return options.slice().sort((a: any, b: any) => {
      return sort(a, b);
    })
  }
  return [];
}
/**
 * 通过搜索条件本地过滤数据
 * @param options 接口返回的全部数据
 * @param otherParams 搜索条件
 * @returns 
 */
export function paramsFilterOptions(options: any[], otherParams?: IObject, excludeFieldname?: string | string[]): any[] {
  if (!otherParams) return options;
  const nonemptyParams = deleteObjectFieldname(otherParams, excludeFieldname);
  const keys = Object.keys(nonemptyParams);
  const values = Object.values(nonemptyParams);
  if (!keys.length) return options;
  return options.filter(item => {
    const itemParamsValue: any[] = [];
    keys.forEach((v: string) => {
      itemParamsValue.push(item[v]);
    });
    return arrayParamsEqual(values, itemParamsValue);
  });
}
/**
 * 过滤不参与本地搜索的参数和空值
 * @param params 搜索参数
 * @param excludeFieldname 不参与本地搜索的参数
 * @returns 
 */
export function deleteObjectFieldname(params: IObject = {}, excludeFieldname?: string | string[]) {
  const newObj: any = {};
  const excludeFieldnames = Array.isArray(excludeFieldname) ? excludeFieldname : (typeof excludeFieldname === 'string' ? [excludeFieldname] : []);
  for (let [key, value] of Object.entries(params)) {
    if (!excludeFieldnames.includes(key)) {
      if (key && value !== undefined && value !== '' && value !== null) {
        newObj[key] = value;
      }
    }
  }
  return newObj;
}
/**
 * 判断两个数组的搜索条件是否匹配
 * @param arr1 搜索条件值数组
 * @param arr2 选项通过搜索条件匹配出来的值数组
 * @returns boolean
 */
export const arrayParamsEqual = (arr1?: any[], arr2?: any[]): boolean => {
  if (Array.isArray(arr1) && Array.isArray(arr2)) {
    if (arr1.length === arr2.length) {
      for (let i = 0; i < arr1.length; i++) {
        const value1 = arr1[i];
        const value2 = arr2[i];
        if (Array.isArray(value1) && typeof value2 === 'string') {
          if (!value1.includes(value2)) return false;
        } else {
          if (value1 !== value2) return false;
        }
      }
      return true;
    }
    return false;
  }
  return arr1 === arr2;
};
/**
 * 将initOptions的值并入options
 * @param options 
 * @param initOptions 
 * @returns 
 */
export function formatOptionsAndInitOptions(
  valueKey: string = DEFAULT_FIELD_NAMES.value,
  options?: any[],
  initOptions?: any[],
) {
  const newOptions = options || [];
  if (Array.isArray(initOptions) && initOptions.length) {
    initOptions.forEach(item => {
      const isHave = newOptions.some(v => v[valueKey] === item[valueKey]);
      if (!isHave) {
        newOptions.unshift(item);
      }
    });
  }
  return newOptions;
}
